import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { PhoneNumberUtil } from "google-libphonenumber";

import { ActionButton, FormDialog, InputField, PhoneInput } from "../v3";
import { HumanResourceService } from "../../services";
import { employeeValidationRules } from "../../pages/employee/validation";

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

export const AddEditEmployee = ({ open, onClose, employeeId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState({
    name: undefined,
    primaryPhone: undefined,
    salary: undefined,
    designation: undefined,
  });

  const isValid = isPhoneValid(employeeDetails.primaryPhone);
  const showError = touched && !isValid;

  useEffect(() => {
    if (open && employeeId) {
      fetchData(employeeId);
    } else if (open && !employeeId) {
      handleClearForm();
    }
  }, [open, employeeId]);

  const fetchData = async (id) => {
    try {
      const response = await HumanResourceService.getEmployeeById(id);
      setEmployeeDetails({
        name: response?.data?.name || undefined,
        primaryPhone: response?.data?.primaryPhone || undefined,
        designation: response?.data?.designation || undefined,
        salary: Number(response?.data?.salary) || undefined,
      });
    } catch (error) {
      toast("Failed to fetch employee details", { type: "error" });
      console.error("Error fetching employee data:", error);
    }
  };

  const handleClearForm = () => {
    setEmployeeDetails({
      name: undefined,
      primaryPhone: null,
      salary: undefined,
    });
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setEmployeeDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const sanitizeEmployeeDetails = (details) => {
    const sanitizedDetails = { ...details };

    Object.keys(sanitizedDetails).forEach((key) => {
      if (sanitizedDetails[key] === "") {
        sanitizedDetails[key] = null;
      }
    });
    return sanitizedDetails;
  };

  const handleSaveEmployee = async () => {
    setErrors({});
    const validationErrors = employeeValidationRules(employeeDetails);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (employeeDetails.salary) employeeDetails.salary = Number(employeeDetails.salary);

    try {
      setLoading(true);
      const payload = sanitizeEmployeeDetails(employeeDetails);
      if (employeeId) await HumanResourceService.updateEmployee(employeeId, payload);
      else
        await HumanResourceService.createEmployee({
          ...payload,
          type: "EMPLOYEE",
        });

      toast.success(`Employee ${employeeId ? "updated" : "added"} successfully`);
      if (onSuccess) onSuccess();
      handleCloseModal();
    } catch (error) {
      let errorMessage = `Error ${employeeId ? "updating" : "adding"} employee`;
      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast(errorMessage, { type: "error" });
      console.error("🚀 ~ handleAddEmployee ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title={employeeId ? "Edit Employee" : "Add Employee"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveEmployee} disabled={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Name"
            name="name"
            value={employeeDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />
          <PhoneInput
            label="Mobile Number"
            defaultCountry="in"
            value={employeeDetails.primaryPhone}
            size="small"
            fullWidth
            required
            onChange={(e164) => setEmployeeDetails((prev) => ({ ...prev, primaryPhone: e164 }))}
            onBlur={() => setTouched(true)}
            error={showError}
            helperText={showError ? "Enter a valid phone number" : " "}
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            label="Salary"
            name="salary"
            type="number"
            value={employeeDetails.salary}
            onChange={updateDetailsState}
            error={!!errors.salary}
            helperText={errors.salary}
            fullWidth
          />
          <InputField
            label="Designation"
            name="designation"
            value={employeeDetails.designation}
            onChange={updateDetailsState}
            error={!!errors.designation}
            helperText={errors.designation}
            fullWidth
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
